import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { Children, createElement, isValidElement, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames/bind';
import { defaultPropObject } from "../constants";
import { useMediaQuery } from "../hooks";
import useDisableScroll from "../hooks/useDisableScroll";
import { ChevronNormalLeft, CloseLinear } from "../icons";
import { ButtonBase } from "../internal/ButtonBase";
import { ClickOutside } from "../internal/ClickOutside";
import CloseButton from "../internal/CloseButton";
import { TextBase } from "../internal/TextBase";
import { canUseDOM } from "../utils/canUseDOM";
import { classGetter } from "../utils/classes";
import noop from "../utils/noop";
import { calculate, setupSwipeDown, transform } from "../utils/setupSwipeDown";
import throttle from "../utils/throttle";
import { isNumber } from "../utils/type";
import { Button } from '../Button';
import Depth from '../Depth';
import Divider from '../Divider';
import Scrollbar from '../Scrollbar';
import Title from '../Title';
import styles from './Modal.css';
var cx = classNames.bind(styles);
var MIN_WIDTH = 250;
var MIN_CONTENT_HEIGHT = 100;
var DEFAULT_MAX_CONTENT_HEIGHT = 600;

var getNode = function getNode(element) {
  return /*#__PURE__*/isValidElement(element) ? element : /*#__PURE__*/createElement(element);
};

var POPUP_IDS = ['DS_Suggest_Popup', 'DS_Select_Popup'];
POPUP_IDS = [].concat(_toConsumableArray(POPUP_IDS), _toConsumableArray(POPUP_IDS.map(function (id) {
  return "".concat(id, "_Overlay");
})));

var Modal = function Modal(_ref) {
  var _objectSpread3;

  var actions = _ref.actions,
      _ref$buttonProps = _ref.buttonProps,
      buttonProps = _ref$buttonProps === void 0 ? defaultPropObject : _ref$buttonProps,
      buttonCancelProps = _ref.buttonCancelProps,
      buttonConfirmProps = _ref.buttonConfirmProps,
      _ref$buttonsOrientati = _ref.buttonsOrientation,
      buttonsOrientation = _ref$buttonsOrientati === void 0 ? 'horizontal' : _ref$buttonsOrientati,
      _ref$cancelLabel = _ref.cancelLabel,
      cancelLabel = _ref$cancelLabel === void 0 ? 'Отменить' : _ref$cancelLabel,
      children = _ref.children,
      _ref$classes = _ref.classes,
      classes = _ref$classes === void 0 ? defaultPropObject : _ref$classes,
      closeButtonStyle = _ref.closeButtonStyle,
      closeButtonProps = _ref.closeButtonProps,
      closeOnOverlayClick = _ref.closeOnOverlayClick,
      closeOutside = _ref.closeOutside,
      _ref$confirmLabel = _ref.confirmLabel,
      confirmLabel = _ref$confirmLabel === void 0 ? 'Применить' : _ref$confirmLabel,
      contentProps = _ref.contentProps,
      _ref$dataQa = _ref.dataQa,
      dataQa = _ref$dataQa === void 0 ? defaultPropObject : _ref$dataQa,
      hideCloseButton = _ref.hideCloseButton,
      id = _ref.id,
      Image = _ref.image,
      isButtonsStretch = _ref.isButtonsStretch,
      isCentered = _ref.isCentered,
      _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? true : _ref$isOpen,
      isStretch = _ref.isStretch,
      maxContentHeight = _ref.maxContentHeight,
      _ref$meta = _ref.meta,
      meta = _ref$meta === void 0 ? defaultPropObject : _ref$meta,
      _ref$mobilePosition = _ref.mobilePosition,
      mobilePosition = _ref$mobilePosition === void 0 ? 'bottom' : _ref$mobilePosition,
      overflow = _ref.overflow,
      onCancel = _ref.onCancel,
      onClose = _ref.onClose,
      onConfirm = _ref.onConfirm,
      onlyButtons = _ref.onlyButtons,
      onOverlayClick = _ref.onOverlayClick,
      onSwipeDown = _ref.onSwipeDown,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      subtitle = _ref.subtitle,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'b2c' : _ref$theme,
      title = _ref.title,
      TitleIcon = _ref.titleIcon,
      topDivider = _ref.topDivider,
      type = _ref.type,
      width = _ref.width,
      withoutTopDividerGap = _ref.withoutTopDividerGap,
      _ref$withScroll = _ref.withScroll,
      withScroll = _ref$withScroll === void 0 ? true : _ref$withScroll,
      _ref$wrapTitle = _ref.wrapTitle,
      wrapTitle = _ref$wrapTitle === void 0 ? true : _ref$wrapTitle,
      contentComponent = _ref.contentComponent,
      titleComponent = _ref.titleComponent,
      titleProps = _ref.titleProps,
      zIndex = _ref.zIndex,
      zIndexOverlay = _ref.zIndexOverlay,
      portal = _ref.portal,
      hideOverlay = _ref.hideOverlay,
      _ref$headerMobile = _ref.headerMobile,
      headerMobile = _ref$headerMobile === void 0 ? 'close' : _ref$headerMobile,
      targetDisableScroll = _ref.targetDisableScroll;
  var breakpoints = useMediaQuery('smMin', 'lg', 'xs', 'sm', 'mdMin');

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      scroll = _useState2[0],
      setScroll = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      hidden = _useState4[0],
      setHidden = _useState4[1];

  var containerRef = useRef(null);
  var contentRef = useRef(null);
  var modalRef = useRef(null);
  var titleRef = useRef(null); // eslint-disable-next-line react-hooks/exhaustive-deps

  var getClass = useCallback(classGetter(classes), [classes]);
  var scrollbarHeight = isNumber(maxContentHeight) && maxContentHeight >= MIN_CONTENT_HEIGHT ? maxContentHeight : DEFAULT_MAX_CONTENT_HEIGHT;
  var isSingleButton = onCancel && !onConfirm || !onCancel && onConfirm;
  var defineSizeCls = useCallback(function (name) {
    var _ref2;

    return _ref2 = {}, _defineProperty(_ref2, "".concat(name, "_autosize"), !size), _defineProperty(_ref2, "".concat(name, "_size_").concat(size), size), _ref2;
  }, [size]);

  var getModalWidthStyles = function getModalWidthStyles() {
    return width && isNumber(width) && width >= MIN_WIDTH ? {
      style: {
        width: width,
        maxWidth: 'inherit'
      }
    } : {};
  };

  useDisableScroll({
    isOpen: !hidden,
    onlyMobile: true,
    target: targetDisableScroll || modalRef.current
  });
  useEffect(function () {
    var container = containerRef.current;
    if (!canUseDOM || !container || !withScroll) return;

    function handleResize() {
      if (container && container.clientHeight >= scrollbarHeight && !scroll) {
        setScroll(true);
      }

      if (container && container.clientHeight < scrollbarHeight && scroll) {
        setScroll(false);
      }
    }

    handleResize();
    var resize = throttle(handleResize, 90);
    window.addEventListener('resize', resize); // eslint-disable-next-line consistent-return

    return function () {
      return window.removeEventListener('resize', resize);
    };
  }, [scroll, scrollbarHeight, withScroll, children]);
  useEffect(function () {
    if (!onSwipeDown) {
      setHidden(!isOpen);
      return;
    }

    transform(contentRef.current, '100%');

    if (isOpen) {
      setHidden(false);
      setTimeout(function () {
        transform(contentRef.current, calculate(contentRef.current, 'auto'));
      }, 110);
    } else {
      setTimeout(function () {
        setHidden(true);
      }, 110);
    }
  }, [isOpen, onSwipeDown]);
  useEffect(function () {
    if (isOpen && onSwipeDown) {
      return setupSwipeDown({
        anchor: titleRef.current,
        element: contentRef.current,
        onSwipeDown: onSwipeDown,
        top: 'auto',
        transformToEnd: true
      });
    }

    return undefined;
  }, [isOpen, onSwipeDown]); // Обработчик для ClickOutside, в случае если внутри модального окна находится Select или PhoneField или Suggest,
  // и при открытии меню на мобильных устройствах, оно появляется в портале за пределами модального окна. Метод
  // нужен для получения информации, что если нажатие было внутри меню, то предотвратить закрытие модального окна

  var handleClose = useCallback(function (e) {
    if (e) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      var path = e.path || e.composedPath && e.composedPath();

      if ( // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      path.some(function (item) {
        return item.dataset && POPUP_IDS.includes(item.dataset.id);
      })) {
        return;
      }

      if (onClose) {
        onClose(e);
      }
    }
  }, [onClose]);

  var handleOverlayClick = function handleOverlayClick(e) {
    if (onOverlayClick) onOverlayClick();
    if (closeOnOverlayClick && onClose) onClose(e);
  };

  var getContentText = function getContentText() {
    var textSize;
    var textAlignment;

    if (size) {
      if (['medium', 'large', 'x-large'].includes(size)) {
        textSize = 'l';
      }
    } else if (breakpoints.smMin) {
      textSize = 'l';
    }

    if (isCentered) {
      textAlignment = 'center';
    }

    return /*#__PURE__*/React.createElement(TextBase, {
      as: "div",
      size: textSize,
      alignment: textAlignment,
      block: true
    }, children);
  };

  var getTitle = function getTitle(text) {
    var isSubTitle = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

    if (titleComponent) {
      return getNode(titleComponent);
    }

    var fontSize = 32;
    var level = 2;

    if ((mobilePosition === 'bottom' || mobilePosition === 'full') && breakpoints.xs) {
      fontSize = 20;
      level = 5;
    } else if (Image) {
      fontSize = 24;

      if (size === 'small') {
        fontSize = 20;
      }
    } else if (size) {
      if (size === 'medium') {
        level = 3;
        fontSize = 24;
      } else if (size === 'small') {
        fontSize = 20;
        level = 3;
      }
    } else if (breakpoints.lg) {
      fontSize = 32;
    } else if (breakpoints.xs) {
      fontSize = 24;
    }

    if (headerMobile === 'back' && breakpoints.xs) {
      fontSize = undefined;
      level = 5;
    }

    return /*#__PURE__*/React.createElement(Title, _extends({}, !isSubTitle && dataQa.title ? {
      'data-qa': dataQa.title
    } : {}, isSubTitle && dataQa.subTitle ? {
      'data-qa': dataQa.subTitle
    } : {}, {
      level: level,
      style: {
        fontSize: fontSize
      }
    }, titleProps), text);
  };

  var getButtonSize = function getButtonSize() {
    var buttonSize;

    if (buttonProps !== null && buttonProps !== void 0 && buttonProps.size) {
      buttonSize = buttonProps.size;
    } else if (Image) {
      if (size) {
        if (size !== 'small') {
          buttonSize = 'large';
        }
      } else if (breakpoints.smMin) {
        buttonSize = 'large';
      }
    } else if (size) {
      if (size === 'medium') {
        buttonSize = 'large';
      } else if (size === 'large' || size === 'x-large') {
        buttonSize = 'x-large';
      }
    } else if (breakpoints.sm) {
      buttonSize = 'large';
    } else if (breakpoints.mdMin) {
      buttonSize = 'x-large';
    }

    return buttonSize;
  };

  var getButtonTextSize = function getButtonTextSize() {
    if (size) {
      if (['medium', 'large', 'x-large'].includes(size)) {
        return 'l';
      }
    } else if (breakpoints.smMin) {
      return 'l';
    }

    return undefined;
  };

  var defaultActions = [/*#__PURE__*/React.createElement(Button, _extends({}, dataQa.cancelButton ? {
    'data-qa': dataQa.cancelButton
  } : {}, {
    theme: "secondary",
    onClick: onCancel,
    size: getButtonSize(),
    block: true
  }, buttonCancelProps), /*#__PURE__*/React.createElement(TextBase, {
    weight: "semibold",
    size: getButtonTextSize()
  }, cancelLabel)), /*#__PURE__*/React.createElement(Button, _extends({}, dataQa.confirmButton ? {
    'data-qa': dataQa.confirmButton
  } : {}, {
    onClick: onConfirm,
    theme: type === 'error' ? 'destructive' : theme,
    size: getButtonSize(),
    block: true
  }, buttonConfirmProps), /*#__PURE__*/React.createElement(TextBase, {
    weight: "semibold",
    color: "W100",
    size: getButtonTextSize()
  }, confirmLabel))];

  if (!onCancel) {
    defaultActions.shift();
  } else if (!onConfirm) {
    defaultActions.pop();
  }

  if ((breakpoints.xs || size === 'small') && buttonsOrientation === 'vertical') {
    defaultActions.reverse();
  }

  var content = contentComponent ? getNode(contentComponent) : getContentText();
  var hasScroll = withScroll && scroll;
  var inner = /*#__PURE__*/React.createElement("div", _extends({}, dataQa.content ? {
    'data-qa': dataQa.content
  } : {}, {
    className: cx('Inner', _objectSpread({
      Inner_scroll: hasScroll || isStretch,
      Inner_topDivider: topDivider,
      Inner_withoutTopDividerGap: withoutTopDividerGap
    }, defineSizeCls('Inner')), classes.inner)
  }, contentProps), content);
  var buttons = null;

  if (onCancel || onConfirm || actions) {
    var _objectSpread2;

    var alignment = buttonProps.alignment;
    buttons = /*#__PURE__*/React.createElement("div", {
      className: cx('Buttons', _objectSpread((_objectSpread2 = {}, _defineProperty(_objectSpread2, "Buttons_orientation_".concat(buttonsOrientation), buttonsOrientation), _defineProperty(_objectSpread2, "Buttons_alignment_".concat(alignment), alignment), _defineProperty(_objectSpread2, "Buttons_centered", isCentered || isSingleButton), _defineProperty(_objectSpread2, "Buttons_scroll", hasScroll || isStretch), _defineProperty(_objectSpread2, "Buttons_stretch", isButtonsStretch), _defineProperty(_objectSpread2, "Buttons_image", Image), _defineProperty(_objectSpread2, "Buttons_onlyButtons", onlyButtons), _objectSpread2), defineSizeCls('Buttons')), getClass('buttons'), classes.buttons)
    }, Children.map(actions || defaultActions, function (button) {
      return /*#__PURE__*/React.createElement("div", {
        className: cx('Button', _objectSpread({
          Button_single: isSingleButton,
          Button_image: Image
        }, defineSizeCls('Button')), getClass('button')),
        role: "button",
        tabIndex: 0,
        onKeyDown: noop
      }, button);
    }));
  }

  var Container = closeOutside && onClose ? ClickOutside : 'div';
  var withCloseIcon = onClose && !hideCloseButton;
  var withTopDivider = hasScroll || topDivider;
  var divider = null;

  if (withTopDivider) {
    divider = /*#__PURE__*/React.createElement("div", {
      className: cx('Divider', defineSizeCls('Divider'), classes.divider)
    }, /*#__PURE__*/React.createElement(Divider, null));
  }

  if (!isOpen) return null;
  var layout = /*#__PURE__*/React.createElement("div", _extends({}, dataQa.root ? {
    'data-qa': dataQa.root
  } : {}, {
    className: cx('root', {
      root_view_portal: portal
    }, classes.root)
  }), !hideOverlay && /*#__PURE__*/React.createElement(React.Fragment, null, (breakpoints.xs && mobilePosition === 'bottom' || portal) && /*#__PURE__*/React.createElement("div", _extends({
    // eslint-disable-line
    role: "button",
    onClick: handleOverlayClick,
    className: cx('overlay', classes.overlay),
    "data-id": id ? "".concat(id, "_Overlay") : undefined
  }, zIndexOverlay ? {
    style: {
      zIndex: zIndexOverlay
    }
  } : {}))), /*#__PURE__*/React.createElement(Container, _extends({
    className: cx('Modal', _objectSpread((_objectSpread3 = {}, _defineProperty(_objectSpread3, "Modal_type_".concat(type), type), _defineProperty(_objectSpread3, "Modal_stretch", isStretch), _defineProperty(_objectSpread3, "Modal_has_scroll", hasScroll), _defineProperty(_objectSpread3, "Modal_swipe", !!onSwipeDown && breakpoints.xs), _defineProperty(_objectSpread3, "Modal_mobile_position_".concat(mobilePosition), mobilePosition), _defineProperty(_objectSpread3, "Modal_meta_".concat(meta.component), meta.component), _objectSpread3), defineSizeCls('Modal')), classes.modal),
    ref: modalRef,
    "data-id": id
  }, closeOutside && onClose && {
    onClickOutside: handleClose
  }, zIndex ? {
    style: {
      zIndex: zIndex
    }
  } : {}, getModalWidthStyles()), /*#__PURE__*/React.createElement(Depth, {
    depth: "z500W",
    enableOverflow: overflow,
    flex: true
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: cx('Wrapper', defineSizeCls('Wrapper'), classes.wrapper)
  }, getModalWidthStyles()), withCloseIcon && /*#__PURE__*/React.createElement(CloseButton, _extends({}, dataQa.closeButton ? {
    'data-qa': dataQa.closeButton
  } : {}, closeButtonProps, {
    className: cx('CloseButton', classes.closeButton),
    onClick: onClose,
    style: closeButtonStyle
  }), /*#__PURE__*/React.createElement(CloseLinear, null)), !onlyButtons && /*#__PURE__*/React.createElement("div", {
    className: cx('Content', classes.content),
    ref: contentRef
  }, Image && /*#__PURE__*/React.createElement("div", {
    className: cx('Image', defineSizeCls('Image'), classes.image)
  }, /*#__PURE__*/React.createElement(Image, null)), (titleComponent || title) && /*#__PURE__*/React.createElement("div", {
    className: cx('Top', _objectSpread({
      Top_divider: withTopDivider,
      Top_closeIcon: withCloseIcon
    }, defineSizeCls('Top')), classes.top)
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('Title', {
      Title_centered: isCentered
    }, classes.title),
    ref: titleRef
  }, TitleIcon && /*#__PURE__*/React.createElement("div", {
    className: cx('Title_Icon', classes.titleIcon)
  }, /*#__PURE__*/React.createElement(TitleIcon, null)), !TitleIcon && headerMobile === 'back' && breakpoints.xs && /*#__PURE__*/React.createElement(ButtonBase, {
    className: cx('button_back_component', classes.buttonBack),
    onClick: onClose
  }, /*#__PURE__*/React.createElement(ChevronNormalLeft, null)), wrapTitle ? getTitle(title) : title), subtitle && /*#__PURE__*/React.createElement("div", {
    className: cx('Subtitle', {
      Subtitle_centered: isCentered
    }, classes.subTitle)
  }, getTitle(subtitle, true))), divider, /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: cx('Text', _objectSpread({
      Text_scroll: hasScroll || isStretch
    }, defineSizeCls('Text')), classes.text)
  }, withScroll ? /*#__PURE__*/React.createElement(Scrollbar, {
    maxHeight: scrollbarHeight
  }, inner) : inner), hasScroll && divider), buttons))));
  return breakpoints.xs && (mobilePosition === 'bottom' || mobilePosition === 'full') || portal ? /*#__PURE__*/ReactDOM.createPortal(layout, document.body) : layout;
};

export default Modal;